// Utility functions for enhanced mappings

/**
 * Mapping-layer confidence, expressed on the canonical **0..1** unit scale
 * (see `src/lib/confidence/scale.ts`). Every framework mapping returns
 * confidence in this unit so callers never have to guess whether a value is
 * a fraction or a percentage — render with `toPercent()`.
 *
 * Floor is 0.5: a mapping always produces *some* answer, so the weakest
 * meaningful claim is a coin flip, not zero.
 */
export function calculateConfidence(score: number, threshold: number = 1.5): number {
  const distance = Math.abs(score);
  return Math.min(1, Math.max(0.5, (distance / threshold) * 0.5 + 0.5));
}

export function calculateWeightedScore(scores: Record<string, number>, traits: { strong_indicators?: string[]; moderate_indicators?: string[]; indicators?: string[] }): number {
  let totalScore = 0;
  let totalWeight = 0;

  // Strong indicators (weight 1.5)
  if (traits.strong_indicators) {
    traits.strong_indicators.forEach(trait => {
      const score = scores[trait] ?? 5;
      totalScore += score * 1.5;
      totalWeight += 1.5;
    });
  }

  // Moderate indicators (weight 1.0)
  if (traits.moderate_indicators) {
    traits.moderate_indicators.forEach(trait => {
      const score = scores[trait] ?? 5;
      totalScore += score * 1.0;
      totalWeight += 1.0;
    });
  }

  // Regular indicators (weight 1.0)
  if (traits.indicators) {
    traits.indicators.forEach(trait => {
      const score = scores[trait] ?? 5;
      totalScore += score * 1.0;
      totalWeight += 1.0;
    });
  }

  return totalWeight > 0 ? totalScore / totalWeight : 5;
}

// =====================================================================
// Shared scoring primitives for "inferred" frameworks
// (Hogwarts House, True Colors, Classical Temperament, Love Languages).
//
// Goals:
//  - Score each candidate as a SIGNED DEVIATION from the user's own trait
//    mean, so candidates aren't all bunched at ~6/10.
//  - Allow ANTI-traits that actively repel a candidate (e.g. high
//    Diplomatic + Communal Navigate should push you AWAY from Slytherin).
//  - Convert raw deviations into a softmax distribution so confidence is
//    expressed on the same 0-100% scale across every framework.
// =====================================================================

export interface DifferentialTraitSet {
  /** Strongly indicative traits (weight 2.0). */
  positive: string[];
  /** Moderately indicative traits (weight 1.0). */
  positiveModerate?: string[];
  /** Anti-traits that actively repel this candidate (weight 1.5, sign-flipped). */
  negative?: string[];
  /**
   * Single canonical trait used as a deterministic tiebreak when two
   * candidates' softmax percentages land within 1 point of each other.
   * Should be the most archetype-defining trait for the candidate.
   */
  signatureTrait?: string;
}

/** Mean of all numeric trait scores in the user's profile. */
export function userTraitMean(scores: Record<string, number>): number {
  const values = Object.values(scores).filter((v): v is number => typeof v === 'number');
  if (values.length === 0) return 5;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Sum of (traitScore - userMean) * weight, with anti-traits subtracted.
 * Returns a signed deviation; 0 means "average across the board".
 */
export function signedDifferentialScore(
  scores: Record<string, number>,
  traits: DifferentialTraitSet,
  globalMean: number
): number {
  let total = 0;

  for (const trait of traits.positive) {
    const s = scores[trait];
    if (typeof s === 'number') total += (s - globalMean) * 2.0;
  }
  if (traits.positiveModerate) {
    for (const trait of traits.positiveModerate) {
      const s = scores[trait];
      if (typeof s === 'number') total += (s - globalMean) * 1.0;
    }
  }
  if (traits.negative) {
    for (const trait of traits.negative) {
      const s = scores[trait];
      if (typeof s === 'number') total -= (s - globalMean) * 1.5;
    }
  }

  return total;
}

export interface SoftmaxRanking<K extends string = string> {
  key: K;
  rawScore: number;
  percentage: number;
  tiebreak?: number;
}

/**
 * Softmax over signed deviation scores. Temperature defaults to 1.5 — high
 * enough to amplify a real winner, low enough that a near-tie stays near 50/50.
 * Percentages are integer-rounded and patched so they always sum to 100.
 */
export function softmaxRankings<K extends string>(
  raw: Array<{ key: K; score: number; tiebreak?: number }>,
  temperature = 1.5
): SoftmaxRanking<K>[] {
  if (raw.length === 0) return [];
  // Numerical stability: subtract max before exponentiating.
  const maxScore = Math.max(...raw.map((r) => r.score));
  const exps = raw.map((r) => ({
    key: r.key,
    rawScore: r.score,
    tiebreak: r.tiebreak ?? 0,
    exp: Math.exp((r.score - maxScore) * temperature),
  }));
  const total = exps.reduce((a, b) => a + b.exp, 0) || 1;

  const ranked: SoftmaxRanking<K>[] = exps
    .map((e) => ({
      key: e.key,
      rawScore: e.rawScore,
      tiebreak: e.tiebreak,
      percentage: Math.round((e.exp / total) * 100),
    }))
    .sort((a, b) => {
      // Primary: softmax %. If within 1pt, fall back to raw score, then
      // signature-trait tiebreak, then stable alphabetical key. This
      // guarantees ties are broken by the user's actual trait profile,
      // never by Object.entries iteration order.
      const dPct = b.percentage - a.percentage;
      if (Math.abs(dPct) > 1) return dPct;
      const dRaw = b.rawScore - a.rawScore;
      if (Math.abs(dRaw) > 0.01) return dRaw;
      const dTie = (b.tiebreak ?? 0) - (a.tiebreak ?? 0);
      if (Math.abs(dTie) > 0.01) return dTie;
      return String(a.key).localeCompare(String(b.key));
    });

  // Patch rounding drift onto the leader so percentages sum to exactly 100.
  const sum = ranked.reduce((a, b) => a + b.percentage, 0);
  if (sum !== 100 && ranked.length > 0) ranked[0].percentage += 100 - sum;

  return ranked;
}

export interface InferredConfidence {
  /** Integer 25-95, equal to the leader's softmax percentage clamped. */
  confidence: number;
  confidenceLabel: 'Low' | 'Medium' | 'High';
  /** True when leader < 40% — UI should show "Mixed: A / B". */
  mixed: boolean;
  /**
   * True when the leader is statistically indistinguishable from the
   * runner-up (within 2 softmax pts AND raw differential < 0.5). UI
   * should refuse to commit to a single label and prompt for more data.
   */
  noSignal: boolean;
}

/**
 * Unified confidence model used by every inferred framework. Reading
 * "72% Slytherin" and "72% Blue" then mean the same thing: 72% of the
 * differential evidence points to that candidate vs. its peers.
 */
export function inferredConfidence(
  rankings: SoftmaxRanking[]
): InferredConfidence {
  if (rankings.length === 0) {
    return { confidence: 25, confidenceLabel: 'Low', mixed: true, noSignal: true };
  }
  const top = rankings[0].percentage;
  const next = rankings[1]?.percentage ?? 0;
  const topRaw = Math.abs(rankings[0].rawScore);
  const confidence = Math.min(95, Math.max(25, top));
  let confidenceLabel: 'Low' | 'Medium' | 'High';
  if (top >= 55) confidenceLabel = 'High';
  else if (top >= 40) confidenceLabel = 'Medium';
  else confidenceLabel = 'Low';
  const noSignal = top - next <= 2 && topRaw < 0.5;
  return { confidence, confidenceLabel, mixed: top < 40, noSignal };
}

// =====================================================================
// Trait evidence resolver — used by InferredDetailView to explain WHY a
// user landed on a particular Hogwarts house, True Color, etc. Returns
// the strongest supporting and opposing traits from the user's actual
// scores against the candidate's roster.
// =====================================================================

export interface InferredEvidenceTrait {
  trait: string;
  /** Raw user score for the trait (1-10). */
  score: number;
  /** Signed deviation from the user's mean — positive = supports. */
  delta: number;
  /** Roster weight (2.0 / 1.0 / 1.5). */
  weight: number;
  /** Whether this trait is a positive indicator or an anti-trait. */
  kind: 'positive' | 'positiveModerate' | 'negative';
}

export interface InferredEvidence {
  /** Top traits whose deviation pushed the user TOWARDS the result. */
  supporting: InferredEvidenceTrait[];
  /** Top traits whose deviation pushed the user AWAY (i.e. low pos or high neg). */
  opposing: InferredEvidenceTrait[];
}

/**
 * Build an evidence breakdown for one candidate (one house, one color, etc.)
 * given the user's trait scores and the candidate's `DifferentialTraitSet`.
 */
export function getInferredEvidence(
  scores: Record<string, number>,
  traits: DifferentialTraitSet,
  globalMean: number
): InferredEvidence {
  const all: InferredEvidenceTrait[] = [];

  const push = (trait: string, weight: number, kind: InferredEvidenceTrait['kind']) => {
    const s = scores[trait];
    if (typeof s !== 'number') return;
    const delta = s - globalMean;
    all.push({ trait, score: s, delta, weight, kind });
  };

  for (const t of traits.positive) push(t, 2.0, 'positive');
  if (traits.positiveModerate) for (const t of traits.positiveModerate) push(t, 1.0, 'positiveModerate');
  if (traits.negative) for (const t of traits.negative) push(t, 1.5, 'negative');

  // Contribution to the candidate's score: positive deviation on a
  // positive trait pushes towards; negative deviation pushes away.
  // For anti-traits the relationship inverts.
  const contribution = (t: InferredEvidenceTrait): number => {
    return t.kind === 'negative' ? -t.delta * t.weight : t.delta * t.weight;
  };

  const sorted = [...all].sort((a, b) => contribution(b) - contribution(a));

  // Supporting: top entries with positive contribution (sizeable signal).
  const supporting = sorted.filter((t) => contribution(t) > 0.4).slice(0, 3);
  // Opposing: bottom entries with negative contribution.
  const opposing = sorted
    .filter((t) => contribution(t) < -0.4)
    .reverse()
    .slice(0, 2);

  return { supporting, opposing };
}

// Re-export weight loader functions for convenience
export { 
  getWeight, 
  getWeightsForMapping, 
  getArrayWeights,
  getCachedWeightOverrides,
  loadWeightOverridesFromDB,
  clearWeightCache,
  refreshWeightCache
} from './weightLoader';